import { Box } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { DisclosureHost } from "../../../../../../../hooks/use-disclosure";
// import { DEF_PAGINATION, getXtenantId } from "../../../../../../../utils/utils";
import Flex from "../../../../../../../components/Flex";
import Text from "../../../../../../../components/Text";
// import { useAppointmentsList } from "../../../../../../../queries/use-get-appointments";
// import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import {
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { CreatePaymentMethodCardData } from "@stripe/stripe-js";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import StyledButtonV2 from "../../../../../../../components/app/StyledButtonV2";
import CustomCardElement from "../../../../../../../components/app/provider/billing/payment/CustomCardElement";
import { CARD_HOLDER_NAME_SCHEMA } from "../../../../../../../components/app/provider/billing/payment/payment-constant";
import { DSCheckbox } from "../../../../../../../components/ds/DSCheckbox";
import StyledFormModal from "../../../../../../../components/globalModal/StyledModal";
import { useSnackbar } from "../../../../../../../hooks/useSnackbar";
// import { useGetPatientPaymentV2 } from "../../../../../../../queries/billings/payments/use-get-patient-payment-v2";
// import { useSetPrimaryCard } from "../../../../../../../queries/billings/payments/use-set-primary-card";
import { getPublicRealm } from "../../../../../../../layout/app/patient-portal/public-view/dob-validation/useValidateDob";
import { usePatientAttachPaymentMethod } from "../../../../../../../queries/stripe/use-attach-payment-card";
import { getStripePromise } from "../../../../../../../queries/stripe/use-stripe";
import {
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
} from "../../../../../../../utils/utils";
interface AddCardModalProps {
  disclosureHost: DisclosureHost;
  // Optional patient id override. When the modal is used outside a patient
  // route (e.g. Schedule Appointment) there is no `patientId` route param, so
  // the caller supplies the selected patient's uuid. Falls back to route param.
  patientId?: string;
  // Optional copy overrides so the same modal can be reused from flows that
  // need their own wording (e.g. the self-pay intake form). Every one of them
  // defaults to the original behaviour, so existing callers are unaffected.
  dialogTitle?: string;
  description?: string;
  saveLabel?: string;
  successMessage?: string;
  errorMessage?: string;
  onSaved?: () => void;
  // Set on unauthenticated pages (the open intake-form link). There is no
  // access token and no `realm` in localStorage there, so the attach call has
  // to skip the bearer header and read the tenant off the hostname instead.
  isPublic?: boolean;
}

const AddCardModalInner = ({
  disclosureHost,
  patientId: patientIdProp,
  dialogTitle = "Add Card",
  description,
  saveLabel = "Save",
  successMessage,
  errorMessage,
  onSaved,
  isPublic,
}: AddCardModalProps) => {
  // const patientData: any = useOutletContext();
  // const navigate = useNavigate();
  const { patientId: patientIdParam } = useParams();
  const patientId = patientIdProp ?? patientIdParam;
  const showSnackbar = useSnackbar();

  const stripe = useStripe();
  const elements = useElements();
  const queryClient = useQueryClient();
  const { mutateAsync: attachPayment } =
    usePatientAttachPaymentMethod(isPublic);
  // Only needed by the removed client-side "mark as primary" workaround; the
  // attach endpoint handles it via the `isPrimary` flag now.
  // const { mutateAsync: setPrimaryCard } = useSetPrimaryCard();
  // const { refetch: refetchCards } = useGetPatientPaymentV2({
  //   pagination: DEF_PAGINATION,
  //   typeOfPayment: "PATIENT",
  //   paymentMethod: "ONLINE",
  //   gridSorting: [],
  //   patientUuid: patientId,
  //   enabled: disclosureHost.opened && !!patientId,
  // });
  const initialValue = {
    cardNumber: undefined,
    cardHolderName: "",
    expiryDate: undefined,
    status: undefined,
    setAsPrimary: false,
  };

  const validationSchema = Yup.object({
    // cardNumber: Yup.string().required().label("Card Number"),
    cardHolderName: CARD_HOLDER_NAME_SCHEMA,
    // expiryDate: Yup.string().required().label("Expiry Date"),
    // status: Yup.string().required().label("Status"),
    // setAsPrimary: Yup.boolean().required().label("Set As Primary"),
  });
  const formik = useFormik({
    initialValues: initialValue,
    validationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  // Formik owns the card holder name, Stripe owns the number/expiry/CVC. Going
  // through formik.submitForm() meant a name error short-circuited the submit,
  // so createPaymentMethod never ran and the Stripe fields stayed silent until
  // the name was fixed. Validating both here surfaces every error on one click.
  async function handleSubmit(_values?: any) {
    if (!stripe) {
      return;
    }
    const cardNumberElement = elements?.getElement(CardNumberElement);
    const formErrors = await formik.validateForm();
    formik.setTouched({ cardHolderName: true }, false);
    // Runs even when the name is invalid: this is what makes Stripe report the
    // incomplete card fields. An unattached payment method is discarded by
    // Stripe, so the extra call is safe.
    const { paymentMethod, error } = await stripe.createPaymentMethod({
      type: "card",
      card: cardNumberElement,
      billing_details: { name: formik.values?.cardHolderName?.trim() },
    } as CreatePaymentMethodCardData);
    // The card holder name error renders inline under the field, so bail out
    // before touching the snackbar or the attach call.
    if (Object.keys(formErrors).length > 0) return;
    if (error) {
      // Card validation errors from Stripe used to be swallowed silently, which
      // left the modal open with no feedback at all.
      showSnackbar({
        type: "error",
        message: errorMessage ?? error?.message ?? "",
      });
      return;
    }
    attachPayment({
      patientUuid: patientId || "",
      paymentMethodId: paymentMethod.id,
      isPrimary: !!formik.values?.setAsPrimary,
      // getXtenantId() only resolves for provider/patient sessions; on the
      // public page it is undefined, so fall back to the hostname realm.
      xTENANTID: isPublic
        ? (getXtenantId() ?? getPublicRealm())
        : getXtenantId(),
    })
      .then((res) => {
        // On /public routes the global refresh-token interceptor turns every
        // failed response into a resolved promise with no payload, so an empty
        // response here means the attach did not go through.
        if (isPublic && !res) {
          showSnackbar({
            type: "error",
            message: errorMessage ?? "something went wrong",
          });
          return;
        }
        showSnackbar(
          successMessage
            ? { type: "success", message: successMessage }
            : getSuccessSnackbar(res)
        );
        queryClient.invalidateQueries({
          queryKey: ["get-patient-payment-v2"],
        });
        queryClient.invalidateQueries({
          queryKey: ["get-patient-cards"],
        });
        // The attach endpoint now takes an `isPrimary` flag, so the card is
        // promoted server-side in the same call. The old client-side
        // workaround below (refetch the list, match on paymentMethodId, then
        // call set-primary) raced the BE write and is no longer needed.
        // if (formik.values?.setAsPrimary) {
        //   const refetched = await refetchCards();
        //   const newCard = refetched?.data?.data?.content?.find(
        //     (card) => card?.paymentMethodId === paymentMethod.id
        //   );
        //   if (newCard?.uuid) {
        //     await setPrimaryCard({
        //       cardUuid: newCard.uuid,
        //       xTENANTID: getXtenantId(),
        //     });
        //     queryClient.invalidateQueries({
        //       queryKey: ["get-patient-payment-v2"],
        //     });
        //     queryClient.invalidateQueries({
        //       queryKey: ["get-patient-cards"],
        //     });
        //   }
        // }
        onSaved?.();
        closeAndResetForm();
      })
      .catch((err) => {
        showSnackbar(
          errorMessage
            ? { type: "error", message: errorMessage }
            : getErrorSnackbar(err)
        );
      });
  }

  function closeAndResetForm() {
    formik.resetForm();
    disclosureHost.close();
  }

  return (
    <StyledFormModal
      open={disclosureHost.opened}
      onClose={closeAndResetForm}
      dialogTitle={dialogTitle}
      dialogAction={
        <Flex justifyContent={"end"} gap={1}>
          <StyledButtonV2
            label="Cancel"
            variant="outlined"
            onClick={closeAndResetForm}
          />
          <StyledButtonV2
            label={saveLabel}
            variant="contained"
            onClick={() => handleSubmit()}
          />
        </Flex>
      }
      sx={{
        "& .MuiDialog-paper": {
          width: "25vw",
          // The fixed height only fits the card element; let the paper grow when
          // a caller adds descriptive copy above it.
          height: description ? "auto" : "44vh",
        },
      }}
    >
      <Box
        sx={{
          pt: 2,
          justifyContent: "center",
          alignItems: "center",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {description && (
          <Text variant="body2" color="textSecondary">
            {description}
          </Text>
        )}
        <CustomCardElement
          cardHolderName={formik.values.cardHolderName}
          cardHolderError={formik.errors.cardHolderName}
          onCardHolderChange={(name) =>
            formik.setFieldValue("cardHolderName", name)
          }
        />
        <Box
          sx={{
            alignSelf: "flex-start",
          }}
        >
          <DSCheckbox
            label="Mark As Primary"
            checked={!!formik.values.setAsPrimary}
            onChange={(e) =>
              formik.setFieldValue("setAsPrimary", e.target.checked)
            }
          />
        </Box>
      </Box>
    </StyledFormModal>
  );
};

const AddCardModal = (props: AddCardModalProps) => {
  return (
    <Elements stripe={getStripePromise()}>
      <AddCardModalInner {...props} />
    </Elements>
  );
};

export default AddCardModal;
